import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import DotsLoading from '../../../components/dotsLoading';
import PrimaryCard from '../../../components/mazraeKit/cards/primaryCard';
import CustomPaginate from '../../../components/mazraeKit/customPaginate';
import CustomSearchBox from '../../../components/mazraeKit/customSearchBox';
import MainFooter from '../../../components/mazraeKit/footers/footerMain';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import NoSearchResult from '../../../components/mazraeKit/noSearchResult';
import useGetAdverisements from '../../../services/hookService/getAdvertisements/useGetAdverisements';
import useGetAStoreAds from '../../../services/hookService/getAStoreAds/useGetAStoreAds';
import UseGetProfileCity from '../../../services/hookService/getProfileCityName/useGetProfileCity';
import {RootState} from '../../../store/redux';
import {CodeToInputGroupName} from '../../../utilities/helper/codeToInputGroupName';
import useDebounce from '../../../utilities/hooks/useDebounce/useDebounce';
import useScrolling from '../../../utilities/hooks/useScrolling/useScrolling';
import InputPostListBottomSheet from './advertisementListBottomSheet';
import {GroupNameToInputName, InputNameToCode} from './utils';
import FiltersAdvertisementList from './advertisementListBottomSheet/FiltersAdvertisementList/FilterAdvertisementList';
import {useAdvertisementFilters} from '../../../hooks/filters/useAdvertisementFilter';

const PER_PAGE_SIZE = 7;

const AdvertisementList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollDivElement = useRef<HTMLDivElement | null>(null);
  const {isScrollingDown} = useScrolling(scrollDivElement);
  const {filters, setFilter} = useAdvertisementFilters();

  const {inputGroupName} = useParams();
  const {state: stateParams} = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [inputValue, setInputValue] = useState<string>(
    stateParams?.searchText || filters.search || '',
  );
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const debouncedValue = useDebounce(inputValue, 2000);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const {locational} = useSelector(
    (state: RootState) => state.searchingLocation,
  );
  const cities = locational.map(item => item.id).toString();

  useEffect(() => {
    setFilter('search', debouncedValue);
  }, [debouncedValue]);

  const {
    data: storeAdsData,
    count: storeAdsTotalCount,
    isLoading: isStoreAdsLoading,
  } = useGetAStoreAds({
    store_guid: location?.state?.storeGuid || null,
    order_by: 'lastest',
    page: currentPage,
    ...(inputGroupName !== 'allNewAds' && {
      advertisement_type: inputGroupName as AdvertisementType,
    }),
    page_size: PER_PAGE_SIZE,
  });

  const {advertisementList, isLoading: isListAdsLoading} = useGetAdverisements(
    {
      advertisement_type: inputGroupName,
      search: filters?.search || undefined,
      selected_cities_id: cities || undefined,
      input_groups_guid: filters.input_group_guid.length
        ? filters.input_group_guid.map(item => item.guid).join(',')
        : undefined,
      ordering: filters?.order_by || undefined,
      has_store: filters?.has_store || undefined,
      has_price: filters?.has_price || undefined,
      start_price: filters?.start_price || undefined,
      end_price: filters?.end_price || undefined,
      has_immediate: filters?.has_immediate || undefined,
      has_special_offer: filters?.has_special_offer || undefined,
      page: currentPage,
      page_size: PER_PAGE_SIZE,
      input_guid: filters.input_guid.length
        ? filters.input_guid.map(item => item.guid).join(',')
        : undefined,
    },
    !location?.state?.storeGuid,
  );

  let isAdsLoading = isListAdsLoading || isStoreAdsLoading;
  return (
    <div className="flex relative flex-col w-full h-dvh">
      <div>
        <CustomHeader
          canScroll={true}
          isScrolling={false}
          title={inputGroupName && GroupNameToInputName[inputGroupName]}
          preIcon="icon-nameArrow---Right"
          isActiveRightIcon
          onClick={() => {
            navigate(-1);
          }}
        />
      </div>

      <div
        className={classNames(
          'overflow-y-hidden px-3 my-3 w-full duration-500 ease-in-out',
          {
            'h-0 opacity-0': isScrollingDown,
            'h-52 opacity-100': !isScrollingDown,
          },
        )}
      >
        <div className="mt-3 mb-6 w-full">
          <CustomSearchBox
            placeholder="جستجو در فروشگاه "
            type="text"
            value={inputValue}
            inputCustomClass="!pr-2 "
            isFloating={false}
            preIcon="icon-nameSearch"
            removeIconClassName="!left-20"
            onChange={onChangeHandler}
            showRemoveIcon={false}
            customComponent={() => (
              <div
                className="flex absolute left-0 top-1/2 items-center px-2 py-2 border-r-2 border-gray-100 -translate-y-1/2"
                onClick={() =>
                  navigate('/choose-location', {
                    state: {from: location?.pathname},
                  })
                }
              >
                <i className="text-xl icon-nameLocation" />
                <span
                  className="text-sm"
                  onClick={() =>
                    navigate('/choose-location', {
                      state: {
                        from: location?.pathname,
                      },
                    })
                  }
                >
                  {locational?.length
                    ? `${locational?.length} شهرستان`
                    : UseGetProfileCity().profileCity}
                </span>
              </div>
            )}
            {...{inputValue, setInputValue}}
          />
          <div className="flex overflow-x-auto gap-2 items-center mt-3 w-full">
            <button
              className="flex gap-2 items-center px-3 py-2 text-gray-700 rounded-full border w-fit"
              onClick={() => setOpenBottomSheet(true)}
            >
              <i className="font-bold icon-nameFilter text-xxl" />
              <span className="text-sm whitespace-nowrap">فیلتر کردن</span>
            </button>
            <div>
              <FiltersAdvertisementList inputGroupName={inputGroupName} />
            </div>
          </div>
        </div>
      </div>

      <section
        className="flex overflow-y-auto flex-col gap-4 px-3 h-full"
        ref={scrollDivElement}
      >
        {isAdsLoading && <DotsLoading />}
        {advertisementList?.results.length || !!storeAdsData?.length ? (
          (location?.state?.storeGuid
            ? storeAdsData
            : advertisementList?.results
          )?.map(
            (
              item: IStoreAd | IInputGroupsAdvertisementResults,
              index: number,
            ) => (
              <PrimaryCard
                {...(item as IInputGroupsAdvertisementResults)}
                key={item.id}
                cardSize="lg"
                onClick={() =>
                  navigate(
                    `/category-card-detail/${
                      CodeToInputGroupName[item.input_group.code]
                    }/${item.id}`,
                  )
                }
              />
            ),
          )
        ) : (
          <NoSearchResult />
        )}
      </section>

      <div
        className={classNames('duration-500 ease-in-out', {
          'overflow-y-hidden my-2 h-0 opacity-0': isScrollingDown,
          'my-3 opacity-100': !isScrollingDown,
        })}
      >
        {(advertisementList?.results || storeAdsData) && (
          <CustomPaginate
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalCount={
              location?.state?.storeGuid
                ? storeAdsTotalCount ?? 0
                : advertisementList?.count ?? 0
            }
            perPageSize={PER_PAGE_SIZE}
          />
        )}
      </div>

      <InputPostListBottomSheet
        openBottomSheet={openBottomSheet}
        setOpenBottomSheet={setOpenBottomSheet}
        code={inputGroupName ? InputNameToCode[inputGroupName] : undefined}
        title={inputGroupName && GroupNameToInputName[inputGroupName]}
        isShowType={true}
        isCategoriesPage={true}
      />

      <MainFooter isScrolling={false} />
    </div>
  );
};

export default AdvertisementList;
